import {
    Avatar,
    Button,
    Checkbox,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetUsersForStorage } from '../../query/resource-hooks/company';
import { useAssignEmployeesToTeamAccount, useGetTeamAccountAssignedEmployees } from '../../query/resource-hooks/teamAccountEmployee';
import { useSuToast } from '../../hooks/useSuToast';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    teamAccountId: number;
}

export const AssignEmployeesToTeamAccountDrawer: React.FC<Props> = ({ isOpen, onClose, teamAccountId }) => {
    const intl = useIntl();
    const toast = useSuToast();
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsersForStorage();
    const { data: assignedData } = useGetTeamAccountAssignedEmployees(teamAccountId, isOpen);
    const assignMutation = useAssignEmployeesToTeamAccount(
        () => {
            toast.success(intl.formatMessage({ id: 'teamAccounts.assignSuccess' }));
            onClose();
        },
        () => {
            toast.error(intl.formatMessage({ id: 'somethingWentWrong' }));
        }
    );

    useEffect(() => {
        if (assignedData?.data) {
            setSelectedIds(assignedData.data.map((emp) => emp.id));
        }
    }, [assignedData]);

    const filteredUsers = (usersData ?? []).filter((u) => u.name.toLowerCase().includes(search.toLowerCase()));

    const toggleUser = (id: number) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
    };

    const hasChanges = () => {
        const currentIds = (assignedData?.data ?? []).map((e) => e.id).sort();
        const newIds = [...selectedIds].sort();
        return JSON.stringify(currentIds) !== JSON.stringify(newIds);
    };

    const handleSave = () => {
        assignMutation.mutate({ teamAccountId, employeeIds: selectedIds });
    };

    return (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader>{intl.formatMessage({ id: 'teamAccounts.assignEmployees.title' })}</DrawerHeader>
                <DrawerBody>
                    <InputGroup mb={3}>
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder={intl.formatMessage({ id: 'search' })}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            borderRadius="full"
                        />
                    </InputGroup>

                    {isLoadingUsers ? (
                        <Spinner display="block" mx="auto" mt={8} />
                    ) : (
                        <VStack align="stretch" spacing={1}>
                            {filteredUsers.map((user) => (
                                <HStack
                                    key={user.id}
                                    p={3}
                                    borderRadius="lg"
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
                                    onClick={() => toggleUser(user.id)}
                                >
                                    <Checkbox
                                        isChecked={selectedIds.includes(user.id)}
                                        onChange={() => toggleUser(user.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        mr={1}
                                    />
                                    <Avatar size="sm" name={user.name} />
                                    <VStack align="start" spacing={0}>
                                        <Text fontWeight="600">{user.name}</Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {intl.formatMessage({ id: user.role })}
                                        </Text>
                                    </VStack>
                                </HStack>
                            ))}
                        </VStack>
                    )}
                </DrawerBody>
                <DrawerFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={assignMutation.isLoading}>
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button
                        colorScheme="primary"
                        onClick={handleSave}
                        isLoading={assignMutation.isLoading}
                        isDisabled={!hasChanges()}
                    >
                        {intl.formatMessage({ id: 'save' })} ({selectedIds.length})
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};
